import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import ErrorMessage from './ErrorMessage';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Preserve New Relic error capture by calling noticeError if available
    if (window.newrelic && typeof window.newrelic.noticeError === 'function') {
      try {
        // Create a comprehensive error object for New Relic
        const errorForNewRelic = {
          message: error.message,
          stack: error.stack,
          name: error.name,
          componentStack: errorInfo.componentStack,
          errorBoundary: this.constructor.name,
          fallbackUI: this.props.fallbackUI ? 'custom' : 'default',
          currentPath: this.props.location?.pathname,
        };

        // Add custom attributes if provided
        if (this.props.errorAttributes) {
          Object.assign(errorForNewRelic, this.props.errorAttributes);
        }

        window.newrelic.noticeError(error, errorForNewRelic);
      } catch (newRelicError) {
        // If New Relic fails, log it but don't break the error boundary
        console.warn('Failed to send error to New Relic:', newRelicError);
      }
    }

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // Call the onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  componentDidUpdate(prevProps) {
    // Reset error boundary state when route changes
    if (prevProps.location?.pathname !== this.props.location?.pathname) {
      if (this.state.hasError) {
        this.setState({
          hasError: false,
          error: null,
          errorInfo: null,
        });
      }
    }
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallbackUI) {
        return this.props.fallbackUI({
          error: this.state.error,
          errorInfo: this.state.errorInfo,
        });
      }

      // Default fallback UI
      return (
        <ErrorMessage
          error={this.state.error}
          errorInfo={this.state.errorInfo}
        />
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallbackUI: PropTypes.func,
  onError: PropTypes.func,
  errorAttributes: PropTypes.object,
  location: PropTypes.object,
};

export default withRouter(ErrorBoundary);
